import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requestSlotConflictRescheduleToken } from "../utils/slotConflictApi";
import "./SlotConflictPanel.css";

/**
 * Slot conflict panel (Part B).
 *
 * Shown wherever the customer can see the affected booking - the post-payment
 * return page first, then My Bookings - so the explanation and the three choices
 * always live together and always say the same thing.
 *
 * Two shapes, driven by `booking.slotConflict.outcome`:
 *   "placement_found" -> we already moved the appointment and are holding it
 *   "parked"          -> nothing free nearby; a human is reaching out
 */
const formatLongDate = (value, timezone) => {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(`${value}T12:00:00`));
  } catch (err) {
    return String(value);
  }
};

const formatClock = (value) => {
  if (!value) return "";
  const [hours, minutes] = String(value).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return String(value);
  const period = hours >= 12 ? "PM" : "AM";
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${period}`;
};

const SlotConflictPanel = ({
  booking,
  timezone,
  onKeep,
  keeping = false,
  keepError = "",
  variant = "full",
}) => {
  const navigate = useNavigate();
  const [choosing, setChoosing] = useState(false);
  const [chooseError, setChooseError] = useState("");

  const conflict = booking?.slotConflict;

  if (!conflict || !conflict.outcome) return null;

  const isParked = conflict.outcome === "parked";
  const isResolved = conflict.resolution && conflict.resolution !== "awaiting_customer";

  const originalLabel = `${formatLongDate(conflict.originalDate, timezone)} at ${formatClock(
    conflict.originalStartTime,
  )}`;
  const proposedLabel = conflict.proposedDate
    ? `${formatLongDate(conflict.proposedDate, timezone)} at ${formatClock(
        conflict.proposedStartTime,
      )}`
    : null;

  const autoRefundLabel = conflict.autoRefundAt
    ? formatLongDate(String(conflict.autoRefundAt).slice(0, 10), timezone)
    : null;

  /**
   * Take the customer to the dedicated slot picker.
   *
   * A signed-in customer gets a freshly minted token so the page opens straight on
   * the calendar. A guest (no session) cannot mint one, so they go to the standalone
   * page and use its booking-number lookup - which also accepts this booking's status.
   */
  const handleChooseAnotherTime = async () => {
    const bookingRef = booking?._id || booking?.bookingId;

    if (!bookingRef || !localStorage.getItem("ogestyle_token")) {
      navigate("/booking/reschedule");
      return;
    }

    setChoosing(true);
    setChooseError("");

    try {
      const { token } = await requestSlotConflictRescheduleToken(bookingRef);
      navigate(`/booking/slot-conflict?token=${encodeURIComponent(token)}`);
    } catch (err) {
      setChooseError(err.message || "Unable to open the time picker.");
    } finally {
      setChoosing(false);
    }
  };

  return (
    <section
      className={`slot-conflict-panel slot-conflict-panel--${isParked ? "parked" : "moved"} ${
        variant === "compact" ? "slot-conflict-panel--compact" : ""
      }`}
      role="status"
      aria-live="polite"
      data-outcome={conflict.outcome}
    >
      <header className="slot-conflict-panel__header">
        <span className="slot-conflict-panel__badge">
          {isParked ? "Action needed" : "Time changed"}
        </span>
        <h2 className="slot-conflict-panel__title">
          {isParked
            ? "We could not find another time for you"
            : "The time you paid for was taken while you were paying"}
        </h2>
      </header>

      <p className="slot-conflict-panel__lead">
        {isParked ? (
          <>
            Someone else booked <strong>{originalLabel}</strong> while your payment was
            processing, and there was no free time with the same professional in the next
            few days.
          </>
        ) : (
          <>
            Someone else booked <strong>{originalLabel}</strong> while your payment was
            processing. Your payment went through, so we have held the next available time
            for you.
          </>
        )}
      </p>

      <dl className="slot-conflict-panel__times">
        <div className="slot-conflict-panel__time-row">
          <dt>You paid for</dt>
          <dd className="slot-conflict-panel__time--old">{originalLabel}</dd>
        </div>
        <div className="slot-conflict-panel__time-row">
          <dt>{isParked ? "We are looking for" : "Held for you"}</dt>
          <dd className="slot-conflict-panel__time--new">
            {proposedLabel || "A time that works for both of you"}
          </dd>
        </div>
      </dl>

      {isParked && (
        <div className="slot-conflict-panel__hold">
          <p>
            <strong>Your payment is being held, not lost.</strong> No service has been
            delivered and nothing has been deducted. We have asked the professional to
            contact you directly.
          </p>
          {autoRefundLabel && (
            <p>
              If it is not sorted out by <strong>{autoRefundLabel}</strong>, your payment is
              refunded automatically &mdash; you do not need to ask.
            </p>
          )}
        </div>
      )}

      {keepError && <p className="slot-conflict-panel__error">{keepError}</p>}
      {chooseError && <p className="slot-conflict-panel__error">{chooseError}</p>}

      {isResolved ? (
        <p className="slot-conflict-panel__resolved">
          {conflict.resolution === "accepted"
            ? "Saved - we will see you then."
            : "This has been resolved."}
        </p>
      ) : (
        <div className="slot-conflict-panel__actions">
          {!isParked && onKeep && (
            <button
              type="button"
              className="slot-conflict-panel__action slot-conflict-panel__action--primary"
              onClick={() => onKeep(booking)}
              disabled={keeping}
            >
              {keeping ? "Saving..." : "Keep this time"}
            </button>
          )}
          <button
            type="button"
            className="slot-conflict-panel__action"
            onClick={handleChooseAnotherTime}
            disabled={choosing}
          >
            {choosing ? "Opening..." : "Choose another time"}
          </button>
          <Link className="slot-conflict-panel__action" to="/refund-service">
            Cancel &amp; refund
          </Link>
        </div>
      )}
    </section>
  );
};

export default SlotConflictPanel;
